import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

export const Timeline = ({ data, heading, description }) => {
  const listRef = useRef(null);
  const containerRef = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (!listRef.current) {
      return undefined;
    }

    const element = listRef.current;
    const updateHeight = () => {
      const rect = element.getBoundingClientRect();
      setHeight(rect.height);
    };

    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);
    window.addEventListener('resize', updateHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, [data.length]);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start 20%', 'end 75%'],
  });

  const heightTransform = useTransform(scrollYProgress, [0, 1], [0, height]);
  const opacityTransform = useTransform(scrollYProgress, [0, 0.08], [0, 1]);

  return (
    <div className="w-full font-sans" ref={containerRef}>
      {(heading || description) && (
        <div className="pb-10 text-center">
          {heading ? (
            <h3 className="text-[1.2rem] md:text-[1.75rem] mb-3 text-[--text-primary] max-w-3xl mx-auto leading-tight font-semibold">
              {heading}
            </h3>
          ) : null}
          {description ? (
            <p className="text-[--text-secondary] text-sm md:text-[0.98rem] max-w-2xl mx-auto leading-relaxed">
              {description}
            </p>
          ) : null}
        </div>
      )}

      <div ref={listRef} className="relative pb-8 md:pb-10">
        {data.map((item, index) => (
          <div
            key={`${item.title}-${index}`}
            className="pt-7 md:pt-10 grid grid-cols-1 md:grid-cols-[1fr_56px_1fr] md:gap-4 lg:gap-6 items-start"
          >
            {index % 2 === 0 ? (
              <>
                {/* Even: content LEFT (col 1), spacer (col 2), step number (col 3) */}
                <div className="relative md:col-start-1 md:ml-auto md:max-w-[30rem] md:pr-8">
                  <h4 className="text-[1.35rem] md:text-[1.7rem] lg:text-[1.9rem] font-semibold text-[--text-primary] leading-tight mb-3 md:mb-4 md:text-right">
                    {item.title}
                  </h4>
                  <span className="hidden md:block absolute right-0 top-[1.75rem] h-px w-7 bg-gradient-to-r from-transparent to-[--accent]" />
                  {item.content}
                </div>
                <div className="hidden md:block md:col-start-2 w-14" />
                <div className="hidden md:flex md:col-start-3 items-start pt-[1.6rem]">
                  <span className="text-[2.4rem] font-semibold font-mono leading-none text-[--border] select-none">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </div>
              </>
            ) : (
              <>
                {/* Odd: content RIGHT (col 3) — placed first in DOM so it renders on mobile */}
                <div className="relative md:col-start-3 md:row-start-1 md:mr-auto md:max-w-[30rem] md:pl-8">
                  <h4 className="text-[1.35rem] md:text-[1.7rem] lg:text-[1.9rem] font-semibold text-[--text-primary] leading-tight mb-3 md:mb-4">
                    {item.title}
                  </h4>
                  <span className="hidden md:block absolute left-0 top-[1.75rem] h-px w-7 bg-gradient-to-l from-transparent to-[--accent]" />
                  {item.content}
                </div>
                <div className="hidden md:block md:col-start-2 md:row-start-1 w-14" />
                <div className="hidden md:flex md:col-start-1 md:row-start-1 items-start justify-end pt-[1.6rem]">
                  <span className="text-[2.4rem] font-semibold font-mono leading-none text-[--border] select-none">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </div>
              </>
            )}
          </div>
        ))}

        <div
          style={{ height: `${height}px` }}
          className="hidden md:block absolute left-1/2 -translate-x-1/2 top-0 overflow-hidden w-[2px] bg-[linear-gradient(to_bottom,var(--tw-gradient-stops))] from-transparent via-[--border] to-transparent [mask-image:linear-gradient(to_bottom,transparent_0%,black_8%,black_92%,transparent_100%)]"
        >
          <motion.div
            style={{ height: heightTransform, opacity: opacityTransform }}
            className="absolute inset-x-0 top-0 w-[2px] bg-gradient-to-b from-[--accent] via-[--accent-hover] to-transparent rounded-full"
          />
        </div>
      </div>
    </div>
  );
};
